import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { fetchUserProfile } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);


  async function loginWithGoogle() {
    try {
      console.log('Attempting Google login');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log('Google login successful, user:', result.user.uid);
      return result.user;
    } catch (err) {
      console.error('Google login error:', err.message);
      setError(err.message);
      throw err;
    }
  }

  async function logout() {
    console.log('Logging out');
    setUserProfile(null);
    setAvailableTeams([]);
    localStorage.removeItem('authToken');
    return signOut(auth);
  }
  
  function switchTeam(teamId) {
    console.log('Switching to team:', teamId);
    
    // Find the selected team in the available teams
    const selectedTeam = availableTeams.find(team => team.teamId === teamId);
    
    if (selectedTeam && userProfile) {
      console.log('Found team to switch to:', selectedTeam);
      
      // Update the user profile with the selected team's ID and name
      const updatedProfile = {
        ...userProfile,
        teamId: selectedTeam.teamId,
        teamName: selectedTeam.teamName
      };
      
      // Update the user profile state
      setUserProfile(updatedProfile);
      return true;
    }
    
    console.warn('Team not found or no user profile!');
    return false;
  }

  async function fetchProfile(user) {
    if (user) {
      try {
        console.log('Fetching profile for user:', user.uid);
        const token = await user.getIdToken();
        console.log('Token obtained');
        localStorage.setItem('authToken', token);
        
        const profile = await fetchUserProfile(token);
        console.log('Profile fetched successfully:', profile);
        
        // Log important details about the user profile
        console.log('User profile details:');
        console.log('- User ID:', profile.userId);
        console.log('- Team ID:', profile.teamId);
        console.log('- Team Name:', profile.teamName);
        console.log('- Has Team:', profile.hasTeam);
        
        // Store all available teams
        if (Array.isArray(profile.teams) && profile.teams.length > 0) {
          console.log('Available teams:', profile.teams);
          setAvailableTeams(profile.teams);
        } else {
          setAvailableTeams([]);
        }
        
        // Check if we're missing team info
        if (!profile.teamId) {
          console.warn('No teamId found in user profile. Auto-provisioning may not have worked.');
          console.warn('Raw profile data:', JSON.stringify(profile));
        }
        
        setUserProfile(profile);
        return profile;
      } catch (err) {
        console.error("Error fetching user profile:", err);
        const errorDetails = {
          message: err.message,
          response: err.response ? {
            status: err.response.status,
            data: err.response.data
          } : null
        };
        console.error("Detailed error:", JSON.stringify(errorDetails));
        setError("Failed to load user profile: " + (err.response?.data?.message || err.message));
        return null;
      }
    }
    return null;
  }

  useEffect(() => {
    console.log('Setting up auth state listener');
    console.log('Current location:', window.location.href);
    console.log('Auth domain:', auth.config.authDomain);
    
    // Log any persistence issues
    console.log('Auth persistence:', auth._persistenceManager?.persistence?.type);
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed, user:', user ? user.uid : 'null');
      setCurrentUser(user);
      if (user) {
        try {
          console.log('User authenticated, fetching profile...');
          await fetchProfile(user);
        } catch (err) {
          console.error('Error in auth state change handler:', err);
          setError(`Authentication error: ${err.message}`);
        }
      } else {
        console.log('No user found in auth state change');
        setUserProfile(null);
      }
      setLoading(false);
    }, (error) => {
      // This is the error handler for onAuthStateChanged
      console.error('Auth state change error:', error);
      setError(`Authentication observer error: ${error.message}`);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    availableTeams,
    loginWithGoogle,
    logout,
    switchTeam,
    refreshProfile: () => fetchProfile(currentUser),
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}